<template>
  <div>
    <div
      v-if="show"
      class="modal-backdrop fade show"
      style="background-color: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px)"
      @click="handleBackdropClick"
    ></div>
    <div
      v-if="show"
      class="email-container show"
      id="email-validation-modal"
      role="dialog"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-header modal-info">
          <h4 class="modal-title">
            <i class="icon-envelope-check"></i>
            Validar {{ title }}
          </h4>
          <button
            v-if="allowClose"
            type="button"
            class="close"
            @click="handleClose"
            aria-label="Close"
            style="
              position: absolute;
              right: 15px;
              top: 15px;
              color: white;
              opacity: 0.8;
              font-size: 28px;
              font-weight: bold;
              background: none;
              border: none;
              cursor: pointer;
            "
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- Loading overlay -->
          <div v-if="loadingChannels" class="loading-overlay">
            <div class="loading-spinner"></div>
            <p class="loading-text">Carregando...</p>
          </div>

          <!-- Etapa 1: Seleção do canal de notificação -->
          <div
            v-if="step === 'channel-selection' && !loadingChannels"
            class="channel-selection-step"
          >
            <h3 class="channel-title">Como gostaria de ser notificado?</h3>
            <p class="channel-subtitle">
              Atenção: Verifique se você tem acesso ao e-mail ou telefone
              cadastrado. Caso não tenha, entre em contato com a Central de
              Relacionamento.
            </p>
            <div v-if="channels.length === 0" class="no-channels">
              <p class="error-message">
                Nenhuma opção de notificação disponível no momento. Entre em
                contato com a Central de Relacionamento.
              </p>
            </div>
            <div v-else class="channel-options">
              <div
                v-for="channel in channels"
                :key="channel.value"
                class="channel-option"
                :class="{
                  'channel-option-selected': selectedChannel === channel.value,
                }"
                @click="selectChannel(channel.value)"
              >
                <div class="channel-icon">
                  <i :class="channel.icon"></i>
                </div>
                <span class="channel-label">{{ channel.label }}</span>
                <div class="channel-radio">
                  <div
                    class="radio-button"
                    :class="{
                      'radio-button-selected':
                        selectedChannel === channel.value,
                    }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-if="channelErrorMessage" class="error-message">
              {{ channelErrorMessage }}
            </div>
            <button
              v-if="!loadingChannels && channels.length > 0"
              class="btn btn-confirm"
              :disabled="!selectedChannel || isProcessing"
              @click="handleConfirmChannel"
            >
              Confirmar
            </button>
            <button
              v-if="allowClose"
              class="btn-cancel-link"
              :disabled="isProcessing"
              @click="handleClose"
            >
              Cancelar
            </button>
          </div>

          <!-- Etapa 2: Inserção do código -->
          <div v-if="step === 'code-input'" class="code-input-step">
            <p class="instruction-text">
              Digite o código de 6 dígitos que enviamos para o seu
              {{ selectedChannelLabel }} para confirmar.
            </p>
            <div class="code-input-container">
              <label class="code-label" for="code-input-1"
                >Digite o código de verificação</label
              >
              <div class="code-input-boxes">
                <input
                  v-for="n in 6"
                  :key="n"
                  :id="n === 1 ? 'code-input-1' : undefined"
                  type="text"
                  class="code-box"
                  :class="{ 'code-box-error': hasError }"
                  maxlength="1"
                  :ref="`codeInput${n}`"
                  v-model="code[n - 1]"
                  @input="handleCodeInput($event, n)"
                  @keydown="handleCodeKeydown($event, n)"
                  @paste="handleCodePaste($event)"
                />
              </div>
            </div>
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            <div class="resend-container">
              <span class="resend-question">Não recebeu o código?</span>
              <button
                class="btn btn-default resend-button"
                :disabled="resendTimer > 0"
                @click="handleResendClick"
              >
                <span v-if="resendTimer > 0"
                  >Reenviar em {{ resendTimer }}s</span
                >
                <span v-else>Reenviar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import auth from "../../services/auth";

export default {
  name: "ModalValidate",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    email: {
      type: String,
      required: true,
      default: "",
    },
    title: {
      type: String,
      default: "e-mail",
    },
    resendCooldown: {
      type: Number,
      default: 60, // segundos
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    allowClose: {
      type: Boolean,
      default: true,
    },
    initialTimer: {
      type: Number,
      default: null, // null significa que deve usar o resendCooldown padrão
    },
  },
  data() {
    return {
      step: "channel-selection", // 'channel-selection' ou 'code-input'
      selectedChannel: null, // 'WHATSAPP', 'SMS', 'EMAIL'
      channelErrorMessage: "",
      isProcessing: false,
      channels: [], // Será preenchido dinamicamente pela API
      loadingChannels: false,
      code: ["", "", "", "", "", ""],
      resendTimer: 0,
      errorMessage: "",
      timerInterval: null,
      hasError: false, // Controla se os inputs devem ter borda vermelha
    };
  },
  computed: {
    selectedChannelLabel() {
      const channel = this.channels.find(
        (c) => c.value === this.selectedChannel
      );
      return channel ? channel.label.toLowerCase() : "";
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset para etapa de seleção de canal
        this.step = "channel-selection";
        this.selectedChannel = null;
        this.channelErrorMessage = "";
        this.resetCode();
        this.errorMessage = "";
        this.isProcessing = false;
        // Busca as opções de notificação disponíveis
        this.fetchNotificationChannels();
        // Previne scroll do body quando modal está aberto
        document.body.style.overflow = "hidden";
      } else {
        // Restaura scroll do body quando modal fecha
        document.body.style.overflow = "auto";
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      }
    },
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    document.body.style.overflow = "auto";
  },
  methods: {
    fetchNotificationChannels() {
      this.loadingChannels = true;
      this.channelErrorMessage = "";

      auth
        .getNotificationStatus()
        .then((response) => {
          this.loadingChannels = false;

          if (!Array.isArray(response)) {
            this.channelErrorMessage =
              "Erro ao carregar opções de notificação.";
            this.channels = [];
            return;
          }

          // Mapeia as descrições da API para os valores esperados
          // Normaliza a descrição para fazer o mapeamento (case-insensitive, remove acentos e espaços)
          const normalizeDescricao = (desc) => {
            return desc
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .replace(/\s+/g, "")
              .replace(/-/g, "");
          };

          const channelMap = {
            whatsapp: {
              value: "WHATSAPP",
              label: "WhatsApp",
              icon: "icon-whatsapp",
            },
            sms: {
              value: "SMS",
              label: "SMS",
              icon: "icon-sms",
            },
            email: {
              value: "EMAIL",
              label: "E-mail",
              icon: "icon-envelope",
            },
            "e-mail": {
              value: "EMAIL",
              label: "E-mail",
              icon: "icon-envelope",
            },
          };

          // Filtra apenas as opções com status 1 (disponíveis)
          this.channels = response
            .filter((item) => item.status === 1)
            .map((item) => {
              const normalized = normalizeDescricao(item.descricao);
              const mapped = channelMap[normalized];
              if (mapped) {
                return mapped;
              }
              // Se não encontrar no mapa, cria um mapeamento genérico
              return {
                value: item.descricao.toUpperCase().replace(/\s+/g, "_"),
                label: item.descricao,
                icon: "icon-envelope",
              };
            })
            .filter((item) => item !== null);

          if (this.channels.length === 0) {
            this.channelErrorMessage =
              "Nenhuma opção de notificação disponível no momento.";
          }
        })
        .catch((err) => {
          this.loadingChannels = false;
          let errorMessage =
            "Erro ao carregar opções de notificação. Tente novamente.";

          if (err.response && err.response.data && err.response.data.status) {
            errorMessage = err.response.data.status.message || errorMessage;
          } else if (err.message) {
            errorMessage = err.message;
          }

          this.channelErrorMessage = errorMessage;
          this.channels = [];
        });
    },
    selectChannel(channel) {
      if (this.isProcessing) return;
      this.selectedChannel = channel;
      this.channelErrorMessage = "";
    },
    handleConfirmChannel() {
      if (!this.selectedChannel || this.isProcessing) {
        if (!this.selectedChannel) {
          this.channelErrorMessage = "Por favor, selecione uma opção";
        }
        return;
      }

      this.isProcessing = true;
      this.channelErrorMessage = "";

      // Emite evento com o canal selecionado
      this.$emit("channel-selected", this.selectedChannel);
    },
    // Método público para avançar para a etapa de código após o canal ser confirmado
    proceedToCodeInput() {
      this.step = "code-input";
      this.isProcessing = false;
      // Inicia o timer de reenvio automaticamente quando avança para código
      const timerValue =
        this.initialTimer !== null ? this.initialTimer : this.resendCooldown;
      this.startResendTimer(timerValue);
      this.$nextTick(() => {
        // Foca no primeiro input quando avança para código
        const firstInput = this.$refs.codeInput1;
        if (firstInput && firstInput[0]) {
          firstInput[0].focus();
        }
      });
    },
    // Método público para voltar à seleção de canal em caso de erro
    resetToChannelSelection() {
      this.step = "channel-selection";
      this.isProcessing = false;
      this.channelErrorMessage = "";
    },
    resetCode() {
      this.code = ["", "", "", "", "", ""];
      this.errorMessage = "";
      this.hasError = false;
    },
    handleCodeInput(event, index) {
      const value = event.target.value.replace(/[^0-9]/g, "");
      this.code[index - 1] = value;

      // Limpa mensagem de erro e estado de erro quando usuário começa a digitar
      if (this.errorMessage) {
        this.errorMessage = "";
        this.hasError = false;
      }

      // Move para o próximo input se digitou um número
      if (value && index < 6) {
        this.$nextTick(() => {
          const nextInput = this.$refs[`codeInput${index + 1}`];
          if (nextInput && nextInput[0]) {
            nextInput[0].focus();
          }
        });
      }

      // Verifica se todos os campos estão preenchidos
      if (this.code.every((digit) => digit !== "")) {
        this.validateCode();
      }
    },
    handleCodeKeydown(event, index) {
      // Backspace: volta para o input anterior se o atual está vazio
      if (event.key === "Backspace" && !this.code[index - 1] && index > 1) {
        this.$nextTick(() => {
          const prevInput = this.$refs[`codeInput${index - 1}`];
          if (prevInput && prevInput[0]) {
            prevInput[0].focus();
          }
        });
      }
      // Delete: limpa o campo atual
      if (event.key === "Delete") {
        this.code[index - 1] = "";
      }
      // Escape: fecha o modal
      if (event.key === "Escape") {
        this.handleClose();
      }
    },
    handleCodePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text")
        .replace(/[^0-9]/g, "");

      if (pastedData.length === 6) {
        this.code = pastedData.split("");
        this.$nextTick(() => {
          // Foca no último input
          const lastInput = this.$refs.codeInput6;
          if (lastInput && lastInput[0]) {
            lastInput[0].focus();
          }
          this.validateCode();
        });
      }
    },
    validateCode() {
      const fullCode = this.code.join("");

      if (fullCode.length === 6) {
        // Emite evento com o código completo
        this.$emit("validate", fullCode);
      }
    },
    handleResendClick() {
      if (this.resendTimer === 0) {
        this.$emit("resend");
        // O timer será reiniciado pelo componente pai após reenviar
      }
    },
    startResendTimer(initialValue) {
      // Usa o valor inicial fornecido ou o resendCooldown padrão
      this.resendTimer =
        initialValue !== undefined ? initialValue : this.resendCooldown;

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // Se o timer já expirou, não inicia
      if (this.resendTimer <= 0) {
        this.resendTimer = 0;
        return;
      }

      this.timerInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      }, 1000);
    },
    handleClose() {
      if (this.allowClose) {
        this.$emit("close");
        this.resetCode();
      }
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop && this.allowClose) {
        this.handleClose();
      }
    },
    // Método público para definir erro
    setError(message) {
      this.errorMessage = message;
      this.hasError = true;
      this.$nextTick(() => {
        const firstInput = this.$refs.codeInput1;
        if (firstInput && firstInput[0]) {
          firstInput[0].focus();
        }
      });
    },
    // Método público para definir estado de erro (com borda vermelha)
    setErrorState(hasError) {
      this.hasError = hasError;
      if (hasError) {
        // Se definir como erro, reseta o código para o usuário tentar novamente
        this.code = ["", "", "", "", "", ""];
        this.$nextTick(() => {
          const firstInput = this.$refs.codeInput1;
          if (firstInput && firstInput[0]) {
            firstInput[0].focus();
          }
        });
      }
    },
    // Método público para limpar erro
    clearError() {
      this.errorMessage = "";
      this.hasError = false;
    },
  },
};
</script>

<style scoped lang="scss">
.error-message {
  color: #d32f2f;
  font-size: 14px;
  text-align: center;
  margin: 16px 0;
  padding: 12px;
  background-color: #ffebee;
  border-radius: 8px;
  border: 1px solid #ffcdd2;
}

.email-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  &.show {
    display: flex !important;
    opacity: 1 !important;
  }
}

.modal-dialog {
  width: 590px;
  max-width: 90%;
  max-height: 90vh;
  text-align: center;
  border-radius: 10px;
  z-index: 1052 !important;
  margin: auto;
  position: relative;
  overflow-y: auto;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% - 40px)) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}
.modal-content {
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: none;
  position: relative;
  z-index: 1052 !important;
  background-color: transparent !important;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
}
.modal-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 32px;
  border-bottom: 0;
  background: linear-gradient(135deg, #653979 0%, #7a4a91 100%);
  position: relative;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
  }
  .modal-title {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 0;
    color: white;
    font-size: 1.25em;
    letter-spacing: 0.5px;
    line-height: 1.4;
    font-weight: 600;
    .icon-envelope-check {
      width: 40px;
      height: 40px;
      background-color: rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 18px;
      font-weight: bold;
      position: relative;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: transform 0.2s ease;
      &:hover {
        transform: scale(1.05);
      }
    }
    .icon-envelope-check::before {
      content: "✉";
      font-size: 18px;
      position: absolute;
    }
    .icon-envelope-check::after {
      content: "✓";
      font-size: 11px;
      position: absolute;
      bottom: 2px;
      right: 2px;
      background-color: #6ea600;
      border-radius: 50%;
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2.5px solid white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }
}
.modal-body {
  padding: 32px;
  background-color: #ffffff;
}
.instruction-text {
  font-size: 15px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 24px 0;
  text-align: center;
  font-weight: 400;
  padding-left: 40px;
  padding-right: 40px;
}
.email-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #444;
  margin: 0 0 32px 0;
  line-height: 1.6;
  text-align: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f6fa 0%, #f0ecf5 100%);
  border-radius: 12px;
  border: 1px solid #e8e0ed;
}
.email-address {
  font-weight: 700;
  color: #653979;
  display: block;
  margin-top: 6px;
  font-size: 15px;
  letter-spacing: 0.3px;
}
.code-input-container {
  margin-bottom: 32px;
}
.code-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 16px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.3px;
}
.code-input-boxes {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
}
.code-box {
  width: 64px;
  height: 64px;
  min-width: 50px;
  max-width: 100%;
  flex: 0 0 auto;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: #333;
  background-color: #fafafa;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #653979;
    background-color: #fff;
    box-shadow: 0 0 0 4px rgba(101, 57, 121, 0.15),
      0 4px 12px rgba(101, 57, 121, 0.2);
    transform: translateY(-2px);
  }
  &:hover:not(:focus) {
    border-color: #b8a5c7;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  &::placeholder {
    color: #ccc;
  }
  &.code-box-error {
    border-color: #d32f2f !important;
    background-color: #ffebee !important;
    box-shadow: 0 0 0 4px rgba(211, 47, 47, 0.15),
      0 2px 4px rgba(211, 47, 47, 0.2) !important;
    &:focus {
      border-color: #d32f2f !important;
      box-shadow: 0 0 0 4px rgba(211, 47, 47, 0.15),
        0 4px 12px rgba(211, 47, 47, 0.2) !important;
    }
  }
}
.resend-container {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  padding-top: 8px;
}
.resend-question {
  font-size: 14px;
  color: #666;
  font-weight: 400;
}
.resend-button {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 24px;
  transition: all 0.3s ease;
  border: 2px solid #653979;
  background-color: transparent;
  color: #653979;
  min-width: 140px;
  &:hover:not(:disabled) {
    background-color: #653979;
    color: #fff;
    border-color: #653979;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(101, 57, 121, 0.3);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #ccc;
    color: #999;
  }
}
// Tablets e telas médias
@media (max-width: 768px) {
  .modal-dialog {
    width: 90%;
    max-width: 500px;
    margin: auto;
  }
  .modal-header {
    padding: 24px 28px;
    .modal-title {
      font-size: 1.15em;
    }
  }
  .modal-body {
    padding: 28px 24px;
  }
  .code-box {
    width: calc((100% - 50px) / 6);
    max-width: 58px;
    min-width: 45px;
    height: 58px;
    font-size: 22px;
  }
  .code-input-boxes {
    gap: 10px;
    padding: 0 10px;
  }
}
// Celulares grandes
@media (max-width: 600px) {
  .modal-dialog {
    width: 95%;
    margin: auto;
  }
  .modal-content {
    border-radius: 12px;
  }
  .modal-header {
    padding: 22px 20px;
    .modal-title {
      font-size: 1.1em;
      gap: 10px;
      flex-wrap: wrap;
      .icon-envelope-check {
        width: 36px;
        height: 36px;
        font-size: 16px;
        &::before {
          font-size: 16px;
        }
        &::after {
          width: 12px;
          height: 12px;
          font-size: 10px;
          border-width: 2px;
        }
      }
    }
  }
  .modal-body {
    padding: 24px 20px;
  }
  .instruction-text {
    font-size: 14px;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .email-info {
    padding: 14px 16px;
    font-size: 13px;
    margin-bottom: 28px;
    border-radius: 10px;
  }
  .email-address {
    font-size: 14px;
    margin-top: 4px;
    word-break: break-word;
  }
  .code-input-container {
    margin-bottom: 28px;
  }
  .code-label {
    font-size: 13px;
    margin-bottom: 14px;
  }
  .code-box {
    width: calc((100% - 40px) / 6);
    max-width: 50px;
    min-width: 40px;
    height: 50px;
    font-size: 20px;
    border-radius: 10px;
    border-width: 1.5px;
    &:focus {
      box-shadow: 0 0 0 3px rgba(101, 57, 121, 0.15),
        0 3px 10px rgba(101, 57, 121, 0.2);
    }
  }
  .code-input-boxes {
    gap: 8px;
    flex-wrap: wrap;
    padding: 0 5px;
  }
  .resend-container {
    flex-direction: column;
    gap: 10px;
    padding-top: 4px;
  }
  .resend-question {
    font-size: 13px;
    text-align: center;
  }
  .resend-button {
    width: 100%;
    min-width: auto;
    padding: 12px 20px;
    font-size: 13px;
  }
}
// Celulares pequenos
@media (max-width: 480px) {
  .modal-dialog {
    width: 95%;
    margin: auto;
  }
  .modal-header {
    padding: 20px 18px;
    flex-shrink: 0;
    .modal-title {
      font-size: 1em;
      gap: 8px;
      .icon-envelope-check {
        width: 32px;
        height: 32px;
        font-size: 14px;
        &::before {
          font-size: 14px;
        }
      }
    }
  }
  .modal-body {
    padding: 20px 18px;
    flex: 1;
    overflow-y: auto;
  }
  .instruction-text {
    font-size: 13px;
    margin-bottom: 18px;
  }
  .email-info {
    padding: 12px 14px;
    font-size: 12px;
    margin-bottom: 24px;
  }
  .email-address {
    font-size: 13px;
  }
  .code-input-container {
    margin-bottom: 24px;
  }
  .code-label {
    font-size: 12px;
    margin-bottom: 12px;
  }
  .code-box {
    width: calc((100% - 30px) / 6);
    max-width: 40px;
    min-width: 35px;
    height: 40px;
    font-size: 18px;
    border-radius: 8px;
  }
  .code-input-boxes {
    gap: 6px;
    padding: 0 5px;
  }
  .resend-container {
    gap: 8px;
  }
  .resend-question {
    font-size: 12px;
  }
  .resend-button {
    padding: 10px 18px;
    font-size: 12px;
  }
}
// Telas muito pequenas (landscape em celulares)
@media (max-width: 360px) {
  .modal-dialog {
    width: 95%;
    margin: auto;
  }
  .modal-header {
    padding: 18px 16px;
    .modal-title {
      font-size: 0.95em;
    }
  }
  .modal-body {
    padding: 18px 16px;
  }
  .code-box {
    width: calc((100% - 25px) / 6);
    max-width: 35px;
    min-width: 30px;
    height: 35px;
    font-size: 16px;
  }
  .code-input-boxes {
    gap: 5px;
    padding: 0 5px;
  }
}
@media (max-width: 900px) and (max-height: 500px) and (orientation: landscape) {
  .modal-header {
    padding: 16px 20px;
  }
  .modal-body {
    padding: 20px;
  }
  .code-box {
    width: calc((100% - 30px) / 6);
    max-width: 48px;
    min-width: 40px;
    height: 48px;
    font-size: 20px;
  }
  .code-input-boxes {
    gap: 6px;
    padding: 0 5px;
  }
  .email-info {
    padding: 12px 16px;
    margin-bottom: 20px;
  }
  .instruction-text {
    margin-bottom: 16px;
  }
}

.modal-backdrop {
  animation: fadeIn 0.3s ease-out;
  transition: opacity 0.3s ease-out;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 1040 !important;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  @media (max-width: 480px) {
    backdrop-filter: blur(2px);
  }
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Estilos para seleção de canal
.channel-selection-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.channel-title {
  font-size: 24px;
  font-weight: 700;
  color: #653979;
  margin: 0 0 12px 0;
  text-align: center;
  line-height: 1.3;
}

.channel-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0 0 32px 0;
  text-align: center;
  line-height: 1.5;
  padding: 0 20px;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e8e0ed;
  border-top-color: #653979;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  color: #888;
  margin: 0;
}

.no-channels {
  width: 100%;
  margin-bottom: 24px;
}

.channel-options {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.channel-option {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #f5f5f5;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 64px;

  &:hover {
    border-color: #b8a5c7;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.channel-option-selected {
    border-color: #653979;
    background-color: #f3eef7;
    box-shadow: 0 0 0 4px rgba(101, 57, 121, 0.15);
  }
}

.channel-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: #653979;
  font-size: 24px;

  .icon-whatsapp::before {
    content: "💬";
    font-size: 24px;
  }

  .icon-sms::before {
    content: "📱";
    font-size: 24px;
  }

  .icon-envelope::before {
    content: "✉";
    font-size: 24px;
  }
}

.channel-label {
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-align: left;
}

.channel-radio {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #999;
  border-radius: 50%;
  background-color: transparent;
  transition: all 0.2s ease;
  position: relative;

  &.radio-button-selected {
    border-color: #653979;
    background-color: #653979;

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: white;
    }
  }
}

.btn-confirm {
  width: 100%;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  background-color: #653979;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  min-height: 48px;

  &:not(:disabled) {
    color: #fff !important;
  }

  &:hover:not(:disabled) {
    background-color: #7a4a91;
    color: #fff !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(101, 57, 121, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #ccc !important;
  }
}

.btn-cancel-link {
  background: none;
  border: none;
  padding: 8px 0;
  margin-top: 4px;
  font-size: 14px;
  color: #999;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #653979;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.code-input-step {
  width: 100%;
}

// Responsividade para seleção de canal
@media (max-width: 768px) {
  .channel-title {
    font-size: 20px;
  }

  .channel-subtitle {
    font-size: 13px;
    padding: 0 10px;
  }

  .channel-option {
    padding: 14px 16px;
    min-height: 56px;
  }

  .channel-icon {
    width: 36px;
    height: 36px;
    margin-right: 12px;
    font-size: 20px;
  }

  .channel-label {
    font-size: 15px;
  }

  .btn-confirm {
    padding: 14px 24px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .channel-title {
    font-size: 18px;
  }

  .channel-subtitle {
    font-size: 12px;
    margin-bottom: 24px;
  }

  .channel-options {
    gap: 12px;
    margin-bottom: 20px;
  }

  .channel-option {
    padding: 12px 14px;
    min-height: 52px;
  }

  .channel-icon {
    width: 32px;
    height: 32px;
    margin-right: 10px;
    font-size: 18px;
  }

  .channel-label {
    font-size: 14px;
  }

  .radio-button {
    width: 18px;
    height: 18px;

    &.radio-button-selected::after {
      width: 6px;
      height: 6px;
    }
  }

  .btn-confirm {
    padding: 12px 20px;
    font-size: 14px;
    min-height: 44px;
  }
}
</style>



// WEBPACK FOOTER //
// modal-validate.vue?18a1f1fa